'use client';
import { useRef, useState } from 'react';
import { Square } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import { formatDuration, parseTs } from '../../lib/format';
import { useNow } from '../../lib/useNow';
import { useOperationDock, type DockChat } from '../../lib/operationDock';
import { apiErrorMessage, elowenClient } from '../../lib/elowenClient';
import { openBrainSession } from '../../lib/brainDock';
import { Button } from '../../components/ui/Button';
import { useToast } from '../../components/ui/Toast';
import { ConversationIconPicker } from '../../components/ui/ConversationIconPicker';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/shadcn/dropdown-menu';

const waiting = (row: DockChat) => row.state === 'question' || row.state === 'permission';

function ChatRow({ row, select }: { row: DockChat; select: () => void }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [stopping, setStopping] = useState(false);
  const title = row.title || t.sessionsPanel.untitled;
  const label = { working: t.chat.activityWorking, background: t.chat.activityBackground,
    question: t.chat.activityQuestion, permission: t.chat.activityPermission,
    done: t.chat.activityDone, failed: t.chat.activityFailed }[row.state];
  // A working run ticks from its durable start; a settled one shows the duration the chat reports for it.
  const live = row.activity?.state === 'working';
  const now = useNow(1000, live);
  const runMs = live ? now - parseTs(row.activity!.at)! : row.activity?.durationMs ?? null;
  return <div className="flex min-w-0 items-center gap-1" data-testid="advisor-chat-row" data-chat-id={row.id} data-state={row.state}>
    <ConversationIconPicker session={row} />
    <DropdownMenuItem className="min-w-0 flex-1 flex-col items-start gap-0.5" onSelect={select}>
      <span className="w-full truncate" title={title}>{title}</span>
      <span className={`text-meta ${row.state === 'failed' ? 'text-destructive' : waiting(row) ? 'text-warning' : 'text-muted-foreground'}`}>
        {label}
        {runMs === null ? null : <> · <span className="tabular-nums">{formatDuration(runMs)}</span></>}
        {row.state === 'done' ? ` · ${t.chat.activityUnread}` : null}
      </span>
      {row.state === 'failed' && row.detail ? <span className="line-clamp-2 break-words text-meta text-destructive" title={row.detail}>{row.detail}</span> : null}
    </DropdownMenuItem>
    {row.canStop ? <DropdownMenuItem variant="destructive" size="icon" disabled={stopping} className="shrink-0"
      aria-label={`${t.brainChat.stop}: ${title}`} title={t.brainChat.stop}
      onSelect={event => {
        // Stopping is an action on the row, not a navigation: the menu stays open on the updated list.
        event.preventDefault();
        setStopping(true);
        void elowenClient.brainAbort(row.id).catch(error => toast(apiErrorMessage(error), 'error', { error })).finally(() => setStopping(false));
      }}>
      <Square size={14} fill="currentColor" aria-hidden />
    </DropdownMenuItem> : null}
  </div>;
}

/**
 * The advisor's round launcher, the one way into the side dock. Conversations needing attention open in
 * the dock through the shared conversation request, which asks for it with `dock`. With no attention rows
 * the mascot directly opens the current assistant.
 * The count and menu use the same operation dock list.
 */
export function AdvisorLauncher({ onOpen }: { onOpen: () => void }) {
  const { t } = useTranslation();
  const { conversations } = useOperationDock();
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  // A chosen row runs after Radix has closed the menu and focus is back on the launcher, so a chat that
  // focuses its composer in response keeps that focus instead of losing it to the restoration.
  const pending = useRef<(() => void) | null>(null);
  const count = conversations.length;
  const tone = conversations.some(row => row.state === 'failed') ? 'text-destructive'
    : conversations.some(waiting) ? 'text-warning' : 'text-foreground';
  const label = count ? `${t.advisor.title} · ${t.advisor.chatsCount.replace('{count}', String(count))}` : t.advisor.title;

  const launcher = <Button ref={trigger} variant="default" size="icon" aria-label={label} title={label}
    className="advisor-launcher operation-dock-button overlay-fab bg-card text-foreground shadow-raised"
    onClick={count ? undefined : onOpen}>
    <img src={'/icon.png'} alt="" className="h-8 w-8 rounded-lg" aria-hidden />
    {count ? <span className={`absolute bottom-1 right-1 rounded-full bg-card px-1 text-small tabular-nums ${tone}`}
      data-testid="advisor-launcher-count" aria-hidden>{count}</span> : null}
  </Button>;
  if (!count) return launcher;
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        {launcher}
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="end" sideOffset={8} collisionPadding={16}
        data-testid="advisor-launcher-menu" className="flex w-[min(--spacing(88),calc(100vw_-_--spacing(8)))] flex-col overflow-hidden"
        onCloseAutoFocus={event => {
          const action = pending.current;
          pending.current = null;
          if (!action) return;
          event.preventDefault();
          trigger.current?.focus({ preventScroll: true });
          action();
        }}>
        <div className="max-h-[min(--spacing(96),60dvh)] min-h-0 overflow-y-auto overscroll-contain" data-testid="advisor-chat-list">
          {conversations.map(row => <ChatRow key={row.id} row={row}
            select={() => { pending.current = () => openBrainSession(row.id, true, { dock: true }); }} />)}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
